import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth, useAlert, useLanguage, useAddNewServiceOffer } from 'hooks';
import { hideModal } from 'state/actions';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import { ALERT, ENG, COLOR } from 'config/constants';
import { HvModal, HvPleaseWait, HvButton, HvDetailedBill } from 'components';
import { ServiceOfferDetails } from 'models';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: 10,
  },
  description: {
    fontSize: 14,
    marginBottom: 10,
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  warning: {
    marginTop: 10,
    color: 'red',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  subtitle: {
    fontFamily: 'FordAntenna-SemiBold',
    marginBottom: 5,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
  itemContent: {
    padding: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

interface Props {
  modalKey: string;
  exampleImage: string;
  headerImage: string;
  modalTitle: string;
  serviceOffer: any;
  shooting_id: number;
}

function OrderServiceOfferModal({
  modalKey,
  exampleImage,
  headerImage,
  modalTitle,
  serviceOffer,
  shooting_id,
}) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const language = useLanguage();
  const { token, user } = useAuth();
  const { displayAlert } = useAlert();
  const [load, setLoad] = useState(false);
  const path = window.location.pathname;
  const arr = path.split('/');
  const distributionToken = arr[arr.length - 1];

  const isEn = language === ENG;

  const { onAddServiceOffer, addedServiceOfferStatus } = useAddNewServiceOffer(
    token,
    distributionToken,
  );
  const [selectedServiceOffer, setSelectedServiceOffer] =
    useState<ServiceOfferDetails>(serviceOffer);
  const [activeStep, setActiveStep] = useState(0);

  const fmtCancel = intl.formatMessage({ id: 'deliver.photos.cancel' });
  const fmtNext = intl.formatMessage({ id: 'deliver.photos.next' });
  const fmtSubmit = intl.formatMessage({ id: 'deliver.photos.submit' });
  const fmtBack = intl.formatMessage({ id: 'back' });
  const fmtSuccess = intl.formatMessage({ id: 'deliver.photos.success' });
  const fmtError = intl.formatMessage({ id: 'deliver.photos.error' });

  useEffect(() => {
    if (addedServiceOfferStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtSuccess);
      dispatch(hideModal(modalKey));
    } else if (addedServiceOfferStatus === 500) {
      displayAlert(ALERT.ERROR, fmtError);
    }
    setLoad(false);
  }, [addedServiceOfferStatus]);

  const onClose = () => {
    dispatch(hideModal(modalKey));
  };
  const onBack = () => {
    if (activeStep === 0) {
      dispatch(hideModal(modalKey));
    } else {
      setActiveStep(activeStep - 1);
    }
  };
  const onNext = () => {
    if (activeStep === 0) {
      setActiveStep(activeStep + 1);
    } else {
      setLoad(true);
      onAddServiceOffer(shooting_id, serviceOffer.id);
    }
  };

  const onChangeOption = () => {
    setSelectedServiceOffer(serviceOffer);
  };

  const buttons = (
    <Box>
      <HvButton className={styles.cancelButton} onClick={onBack}>
        {activeStep ? fmtBack : fmtCancel}
      </HvButton>
      <HvButton onClick={onNext} disabled={!selectedServiceOffer}>
        {activeStep ? fmtSubmit : fmtNext}
      </HvButton>
    </Box>
  );

  const loadServiceOffer = () => {
    return (
      <div className={styles.content}>
        <Typography className={styles.description}>
          {isEn ? serviceOffer?.example?.description : serviceOffer?.example?.description_fr}
        </Typography>
        {serviceOffer?.example?.image_url ? (
          <img src={serviceOffer?.example?.image_url} alt='measures' />
        ) : null}
        <div className={styles.itemContent}>
          <div>
            <Typography>
              {isEn ? serviceOffer?.display_name : serviceOffer?.display_name_fr}
            </Typography>
            <Typography>{`${serviceOffer?.price} $`}</Typography>
          </div>
          <div>
            {selectedServiceOffer?.id === serviceOffer?.id ? (
              <CheckCircleIcon color='secondary' />
            ) : (
              <RadioButtonUncheckedOutlinedIcon onClick={onChangeOption} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const loadReview = () => {
    const serviceOffers = [serviceOffer];
    return <HvDetailedBill service_offers={serviceOffers} />;
  };

  const loadContent = () => {
    switch (activeStep) {
      case 0:
        return loadServiceOffer();
      case 1:
        return loadReview();
      default:
        return loadServiceOffer();
    }
  };
  return (
    <HvModal title={modalTitle} headerImage={headerImage} buttons={buttons} onClose={onClose}>
      {loadContent()}
      {load ? <HvPleaseWait /> : null}
    </HvModal>
  );
}
export default OrderServiceOfferModal;
